import { useId, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Icon,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TooltipProvider,
  cn,
} from "@nexus/ui";
import type { TFunction } from "i18next";
import type { FeatureResultState, ProjectedFeature } from "@nexus/contracts";
import {
  RESULT_STATE_META,
  TONE_ROW_BG,
  TONE_TEXT,
} from "../../constants/result-state";
import {
  criterionRowId,
  featureDisplayState,
  featureDisplayTone,
  featureNote,
  isFeatureVisible,
} from "../../lib/feature-format";
import { CriterionLabel } from "../CriterionLabel/CriterionLabel";

type SortColumn = "criterion" | "result" | "weight";
type SortState = { column: SortColumn; direction: "asc" | "desc" };

/** Result ordering for sorting: best outcome first, the three excluded states last. */
const RESULT_RANK: Record<FeatureResultState, number> = {
  PASS: 0,
  PARTIAL: 1,
  FAIL: 2,
  NOT_WEIGHTED: 3,
  NOT_APPLICABLE: 4,
  NOT_ANALYZED: 5,
};

function compareFeatures(
  a: ProjectedFeature,
  b: ProjectedFeature,
  column: SortColumn,
): number {
  switch (column) {
    case "criterion":
      return a.name.localeCompare(b.name);
    case "weight":
      return a.weight - b.weight;
    case "result":
      // Rank by the DISPLAY state so an unweighted-but-scored feature (shown as Pass/Fail,
      // AD-1723) sorts with Pass/Fail rather than by its raw NOT_WEIGHTED state.
      return (
        RESULT_RANK[featureDisplayState(a)] - RESULT_RANK[featureDisplayState(b)]
      );
  }
}

/** Sort a feature list by the active sort (shared by the active and inactive tables). */
function sortFeatures(
  features: ProjectedFeature[],
  sort: SortState | null,
): ProjectedFeature[] {
  if (!sort) return features;
  return [...features].sort((a, b) => {
    const result = compareFeatures(a, b, sort.column);
    return sort.direction === "asc" ? result : -result;
  });
}

function SortHeader({
  label,
  column,
  sort,
  onSort,
}: {
  label: string;
  column: SortColumn;
  sort: SortState | null;
  onSort: (column: SortColumn) => void;
}) {
  const isActive = sort?.column === column;
  const iconName = isActive
    ? sort.direction === "asc"
      ? "chevron-up"
      : "chevron-down"
    : "chevrons-up-down";

  return (
    <TableHead
      aria-sort={
        isActive
          ? sort.direction === "asc"
            ? "ascending"
            : "descending"
          : "none"
      }
      className="text-muted-foreground text-[11px] font-medium tracking-wide uppercase"
    >
      <button
        type="button"
        onClick={() => onSort(column)}
        className={cn(
          "group -mx-1 inline-flex items-center gap-1 rounded-sm px-1 py-0.5 transition-colors outline-none hover:text-foreground focus-visible:text-foreground focus-visible:ring-ring/40 focus-visible:ring-[3px]",
          isActive && "text-foreground",
        )}
      >
        {label}
        <Icon
          name={iconName}
          size="xs"
          aria-hidden
          className={cn(
            "shrink-0 transition-opacity",
            isActive
              ? "text-primary opacity-100"
              : "text-muted-foreground/50 opacity-0 group-hover:opacity-60",
          )}
        />
      </button>
    </TableHead>
  );
}

/**
 * Shared fixed column widths. The inactive criteria render in a separate nested table
 * (so the collapse can animate), which would otherwise auto-size its columns independently
 * and drift out of alignment with the active table. Applying the same colgroup + table-fixed
 * to both keeps the two tables' columns lined up.
 */
function CriteriaColumns() {
  return (
    <colgroup>
      <col className="w-[42%]" />
      <col className="w-[13%]" />
      <col className="w-[13%]" />
      <col className="w-[32%]" />
    </colgroup>
  );
}

function formatWeight(weight: number, dash: string): string {
  if (weight <= 0) return dash;
  const pct = weight <= 1 ? weight * 100 : weight;
  return `${pct.toFixed(2)}%`;
}

function ResultCell({ feature }: { feature: ProjectedFeature }) {
  const { t } = useTranslation();
  // Display state promotes unweighted-but-scored features to PASS/FAIL (AD-1723); the
  // "Inactive — does not affect score" note (featureNote, raw state) keeps them clearly informational.
  const displayState = featureDisplayState(feature);
  const meta = RESULT_STATE_META[displayState];
  const dash = t("preflight.dash");

  if (meta.labelKey == null) {
    return <span className="text-muted-foreground">{dash}</span>;
  }

  const label =
    displayState === "PARTIAL" && feature.featureScore != null
      ? feature.featureScore.toFixed(1)
      : t(meta.labelKey);

  return (
    <span
      className={cn("inline-flex items-center gap-1.5", TONE_TEXT[meta.tone])}
    >
      <Icon name={meta.icon} size="xs" color="inherit" aria-hidden />
      <span className="tabular-nums">{label}</span>
    </span>
  );
}


function FeatureRow({
  feature,
  activeKey,
  onHover,
  rowIdPrefix,
  dash,
  t,
}: {
  feature: ProjectedFeature;
  activeKey: string | null;
  onHover?: (key: string | null) => void;
  rowIdPrefix?: string;
  dash: string;
  t: TFunction;
}) {
  const key = feature.code ?? feature.name;
  const note = featureNote(feature, t);
  return (
    <TableRow
      id={rowIdPrefix ? criterionRowId(rowIdPrefix, key) : undefined}
      className={cn(
        "transition-colors duration-300 motion-reduce:transition-none",
        onHover && "cursor-default",
        activeKey === key
          ? "bg-muted shadow-[inset_2px_0_0_0_var(--primary)]"
          : TONE_ROW_BG[featureDisplayTone(feature)],
      )}
      onMouseEnter={() => onHover?.(key)}
      onMouseLeave={() => onHover?.(null)}
    >
      <TableCell>
        <CriterionLabel
          name={feature.name}
          definition={feature.definition}
          className="text-foreground font-medium"
        />
      </TableCell>
      <TableCell>
        <ResultCell feature={feature} />
      </TableCell>
      <TableCell className="text-muted-foreground tabular-nums">
        {formatWeight(feature.weight, dash)}
      </TableCell>
      <TableCell className="text-muted-foreground text-sm">{note}</TableCell>
    </TableRow>
  );
}

export interface CriteriaBreakdownTableProps {
  features: ProjectedFeature[];
  /** Criterion key (`code ?? name`) currently hovered elsewhere (e.g. the meter) — highlights its row. */
  activeKey?: string | null;
  /** Notifies the parent which criterion row is hovered, for cross-highlighting the meter. */
  onHover?: (key: string | null) => void;
  /** When set, each row gets a stable DOM id (`<prefix>-<key>`) so other views can scroll to it. */
  rowIdPrefix?: string;
}

/**
 * Renders the server-projected `asset.features` (`ProjectedFeature[]`) directly — the BFF
 * runs `projectFeatures()` server-side, so the FE never re-projects (KTD8). Switches on the
 * six `FeatureResultState` cases; the three excluded states render a muted "—" + note.
 */
export function CriteriaBreakdownTable({
  features,
  activeKey = null,
  onHover,
  rowIdPrefix,
}: CriteriaBreakdownTableProps) {
  const { t } = useTranslation();
  const dash = t("preflight.dash");
  const inactivePanelId = useId();
  // Default to Result-ascending so passing criteria surface first in both tables (RESULT_RANK
  // puts PASS first). Users can still re-sort by clicking any column header.
  const [sort, setSort] = useState<SortState | null>({
    column: "result",
    direction: "asc",
  });
  // Inactive criteria (the three excluded states) don't affect the score, so they're
  // tucked into a collapsed group — present for completeness, but out of the way.
  const [showInactive, setShowInactive] = useState(false);

  const handleSort = (column: SortColumn) =>
    setSort((prev) =>
      prev?.column === column
        ? { column, direction: prev.direction === "asc" ? "desc" : "asc" }
        : { column, direction: "asc" },
    );

  const { activeFeatures, inactiveFeatures } = useMemo(() => {
    const active: ProjectedFeature[] = [];
    const inactive: ProjectedFeature[] = [];
    for (const feature of features) {
      if (RESULT_STATE_META[feature.state].excluded) inactive.push(feature);
      else active.push(feature);
    }
    return { activeFeatures: active, inactiveFeatures: inactive };
  }, [features]);

  // Only surface inactive criteria that actually have a verdict (PASS/FAIL/PARTIAL) to show.
  // Grouping uses the raw state, but an unweighted-but-scored feature promotes to
  // PASS/FAIL/PARTIAL for DISPLAY (AD-1723); criteria whose DISPLAY state is still excluded
  // render a muted "—" with no result, so we hide them from the nested table entirely. When
  // none remain, the toggle button doesn't render.
  const visibleInactiveFeatures = useMemo(
    () => inactiveFeatures.filter(isFeatureVisible),
    [inactiveFeatures],
  );

  const sortedActive = useMemo(
    () => sortFeatures(activeFeatures, sort),
    [activeFeatures, sort],
  );
  const sortedInactive = useMemo(
    () => sortFeatures(visibleInactiveFeatures, sort),
    [visibleInactiveFeatures, sort],
  );

  return (
    <TooltipProvider delayDuration={250} skipDelayDuration={100}>
      <Table className="table-fixed">
        <CriteriaColumns />
        <TableHeader>
          <TableRow>
            <SortHeader
              label={t("preflight.criteria.criterion")}
              column="criterion"
              sort={sort}
              onSort={handleSort}
            />
            <SortHeader
              label={t("preflight.criteria.result")}
              column="result"
              sort={sort}
              onSort={handleSort}
            />
            <SortHeader
              label={t("preflight.criteria.weight")}
              column="weight"
              sort={sort}
              onSort={handleSort}
            />
            <TableHead className="text-muted-foreground text-[11px] font-medium tracking-wide uppercase">
              {t("preflight.criteria.notes")}
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {sortedActive.map((feature) => (
            <FeatureRow
              key={feature.code ?? feature.name}
              feature={feature}
              activeKey={activeKey}
              onHover={onHover}
              rowIdPrefix={rowIdPrefix}
              dash={dash}
              t={t}
            />
          ))}

          {visibleInactiveFeatures.length > 0 ? (
            <>
              <TableRow className="hover:bg-transparent">
                <TableCell colSpan={4} className="p-0">
                  <button
                    type="button"
                    aria-expanded={showInactive}
                    aria-controls={inactivePanelId}
                    onClick={() => setShowInactive((prev) => !prev)}
                    className="text-muted-foreground hover:text-foreground focus-visible:ring-ring/40 flex w-full items-center gap-1.5 px-2 py-2 text-xs outline-none focus-visible:ring-[3px]"
                  >
                    <Icon
                      name="chevron-down"
                      size="xs"
                      color="inherit"
                      className={cn(
                        "transition-transform duration-200 motion-reduce:transition-none",
                        showInactive ? "" : "-rotate-90",
                      )}
                    />
                    {t(
                      showInactive
                        ? "preflight.criteria.hideInactive"
                        : "preflight.criteria.showInactive",
                      { count: visibleInactiveFeatures.length },
                    )}
                  </button>
                </TableCell>
              </TableRow>
              <TableRow className="hover:bg-transparent border-b-0">
                <TableCell colSpan={4} className="p-0">
                  <div
                    id={inactivePanelId}
                    className={cn(
                      "grid transition-[grid-template-rows] duration-300 ease-out motion-reduce:transition-none",
                      showInactive ? "grid-rows-[1fr]" : "grid-rows-[0fr]",
                    )}
                  >
                    <div className="overflow-hidden">
                      <table className="w-full caption-bottom text-sm table-fixed">
                        <CriteriaColumns />
                        <tbody>
                          {sortedInactive.map((feature) => (
                            <FeatureRow
                              key={feature.code ?? feature.name}
                              feature={feature}
                              activeKey={activeKey}
                              onHover={onHover}
                              rowIdPrefix={rowIdPrefix}
                              dash={dash}
                              t={t}
                            />
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </TableCell>
              </TableRow>
            </>
          ) : null}
        </TableBody>
      </Table>
    </TooltipProvider>
  );
}
